// Embedded @font-face fonts (data: URLs only). Design tools such as Figma/Illustrator exports embed
// the brand fonts so the file renders anywhere; we hand them to the builder's own custom-font store.

export interface EmbeddedFont {
  readonly name: string; // builder font name, unique per face, e.g. "Matter-Bold"
  readonly family: string;
  readonly weight: number; // as declared, e.g. 670
  readonly italic: boolean;
  readonly mimeType: string; // font/ttf | font/otf | font/woff | font/woff2
  readonly dataUrl: string;
}

export const FONT_LIMITS = {
  maxFaces: 16,
  maxFontBytes: 4 * 1024 * 1024,
} as const;

const SAFE_FAMILY_RE = /^[\w -]{1,64}$/;
const FONT_DATA_RE =
  /^data:(?:font\/[\w.+-]+|application\/(?:x-)?font-[\w.+-]+|application\/vnd\.ms-(?:opentype|fontobject)|application\/octet-stream)(?:;[\w=.-]+)*;base64,([a-z0-9+/=\s]+)$/i;

const WEIGHT_NAMES: Readonly<Record<number, string>> = {
  100: "Thin",
  200: "ExtraLight",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "SemiBold",
  700: "Bold",
  800: "ExtraBold",
  900: "Black",
};

// Font files are identified by their first bytes, never by the declared MIME type.
const MAGIC: ReadonlyArray<readonly [string, string]> = [
  ["00010000", "font/ttf"],
  ["74727565", "font/ttf"], // "true"
  ["4f54544f", "font/otf"], // "OTTO"
  ["774f4646", "font/woff"], // "wOFF"
  ["774f4632", "font/woff2"], // "wOF2"
];

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

const headHex = (base64: string): string => {
  const chars = base64.slice(0, 8);
  let bits = 0;
  let value = 0;
  let hex = "";
  for (const ch of chars) {
    const v = B64.indexOf(ch);
    if (v === -1) return "";
    value = (value << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      hex += ((value >> bits) & 0xff).toString(16).padStart(2, "0");
    }
  }
  return hex.slice(0, 8);
};

export const sniffFontType = (base64: string): string | null =>
  MAGIC.find(([magic]) => headHex(base64) === magic)?.[1] ?? null;

export const parseFontWeight = (value: string | undefined): number => {
  const raw = (value ?? "").trim().toLowerCase();
  if (raw === "bold" || raw === "bolder") return 700;
  if (raw === "lighter") return 300;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.min(1000, Math.max(1, n)) : 400;
};

export const unquoteFamily = (value: string): string =>
  value
    .split(",")[0]
    .trim()
    .replace(/^['"]|['"]$/g, "");

interface Face {
  readonly family: string;
  readonly weight: number;
  readonly italic: boolean;
  readonly mimeType: string;
  readonly base64: string;
}

const declaration = (block: string, key: string): string | undefined =>
  new RegExp(`(?:^|[;{\\s])${key}\\s*:\\s*([^;}]+)`, "i").exec(block)?.[1]?.trim();

/** Reads @font-face blocks; data: fonts are validated, anything else is reported via `warn`. */
export const extractFontFaces = (css: string, warn: (message: string) => void): Face[] => {
  const faces: Face[] = [];
  const blockRe = /@font-face\s*\{([^{}]*)\}/gi;
  const cleaned = css.replace(/\/\*[\s\S]*?\*\//g, "");
  for (let match = blockRe.exec(cleaned); match; match = blockRe.exec(cleaned)) {
    const block = match[1];
    const family = unquoteFamily(declaration(block, "font-family") ?? "");
    // A data: URL contains ";", so src is matched as a url(...) token rather than a declaration.
    const urls = Array.from(block.matchAll(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi)).map((m) => m[2].trim());
    const data = urls.map((u) => FONT_DATA_RE.exec(u)).find(Boolean);
    if (!data) {
      if (urls.length) warn("Linked fonts were skipped for security");
      continue;
    }
    const base64 = data[1].replace(/\s+/g, "");
    const mimeType = sniffFontType(base64);
    if (!SAFE_FAMILY_RE.test(family) || !mimeType) {
      warn("An embedded font was not a valid font file and was skipped");
      continue;
    }
    if ((base64.length * 3) / 4 > FONT_LIMITS.maxFontBytes) {
      warn("An embedded font was too large and was skipped");
      continue;
    }
    if (faces.length >= FONT_LIMITS.maxFaces) {
      warn(`Only the first ${FONT_LIMITS.maxFaces} embedded fonts were kept`);
      break;
    }
    faces.push({
      family,
      weight: parseFontWeight(declaration(block, "font-weight")),
      italic: /italic|oblique/i.test(declaration(block, "font-style") ?? ""),
      mimeType,
      base64,
    });
  }
  return faces;
};

/** Keeps embedded faces and resolves which one a text run uses (same family, closest weight). */
export class FontRegistry {
  private readonly faces: Face[] = [];
  private readonly used = new Map<Face, EmbeddedFont>();

  add(faces: readonly Face[]): void {
    faces.forEach((face) => {
      if (this.faces.length < FONT_LIMITS.maxFaces) this.faces.push(face);
    });
  }

  /** The builder font name for a text run, or undefined when no embedded face matches. */
  resolve(family: string, weight: number, italic: boolean): string | undefined {
    const candidates = this.faces.filter((f) => f.family.toLowerCase() === family.toLowerCase());
    if (candidates.length === 0) return undefined;
    const sameStyle = candidates.filter((f) => f.italic === italic);
    const pool = sameStyle.length ? sameStyle : candidates;
    const face = pool.reduce((best, f) => (Math.abs(f.weight - weight) < Math.abs(best.weight - weight) ? f : best));
    return this.use(face).name;
  }

  private use(face: Face): EmbeddedFont {
    const existing = this.used.get(face);
    if (existing) return existing;
    const taken = new Set(Array.from(this.used.values()).map((f) => f.name.toLowerCase()));
    const rounded = Math.min(900, Math.max(100, Math.round(face.weight / 100) * 100));
    const suffix = `${rounded === 400 ? "" : WEIGHT_NAMES[rounded]}${face.italic ? "Italic" : ""}`;
    let name = suffix ? `${face.family}-${suffix}` : face.family;
    if (taken.has(name.toLowerCase())) name = `${face.family}-${Math.round(face.weight)}${face.italic ? "Italic" : ""}`;
    const font: EmbeddedFont = {
      name,
      family: face.family,
      weight: face.weight,
      italic: face.italic,
      mimeType: face.mimeType,
      dataUrl: `data:${face.mimeType};base64,${face.base64}`,
    };
    this.used.set(face, font);
    return font;
  }

  usedFonts(): EmbeddedFont[] {
    return Array.from(this.used.values());
  }
}
